import React, { useEffect, useState } from "react";
import { apiAdmin, api, formatCLP, BACKEND_URL } from "../../lib/api";
import { Eye, Trash2, X, FileText, Download, Truck, Upload, Save } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["pendiente", "confirmado", "en_proceso", "enviado", "entregado", "cancelado"];

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [selected, setSelected] = useState(null);
  const [shipForm, setShipForm] = useState({ shipping_company: "", tracking_code: "", tracking_url: "", shipping_proof_path: "", shipping_proof_url: "" });
  const [uploading, setUploading] = useState(false);
  const [savingShip, setSavingShip] = useState(false);

  const load = () => apiAdmin.get("/orders").then((r) => setOrders(r.data));
  useEffect(() => { load(); }, []);

  // When opening order modal, sync shipping form
  useEffect(() => {
    if (selected) {
      setShipForm({
        shipping_company: selected.shipping_company || "",
        tracking_code: selected.tracking_code || "",
        tracking_url: selected.tracking_url || "",
        shipping_proof_path: selected.shipping_proof_path || "",
        shipping_proof_url: selected.shipping_proof_url || "",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.id]);

  const updateStatus = async (id, status) => {
    await apiAdmin.put(`/orders/${id}/status`, { status });
    toast.success("Estado actualizado");
    load();
    if (selected && selected.id === id) setSelected({ ...selected, status });
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este pedido?")) return;
    await apiAdmin.delete(`/orders/${id}`);
    toast.success("Pedido eliminado");
    load();
  };

  const onUploadProof = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/upload-public", fd);
      setShipForm((f) => ({
        ...f,
        shipping_proof_path: res.data.storage_path,
        shipping_proof_url: `${BACKEND_URL}${res.data.url}`,
      }));
      toast.success("Foto cargada. Recuerda GUARDAR.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al subir foto");
    } finally { setUploading(false); }
  };

  const saveShipping = async () => {
    if (!selected) return;
    setSavingShip(true);
    try {
      const payload = {
        shipping_company: shipForm.shipping_company || "",
        tracking_code: shipForm.tracking_code || "",
        tracking_url: shipForm.tracking_url || "",
      };
      if (shipForm.shipping_proof_path) payload.shipping_proof_path = shipForm.shipping_proof_path;
      const res = await apiAdmin.put(`/orders/${selected.id}/shipping`, payload);
      setSelected(res.data);
      toast.success("Envío actualizado ✓");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar envío");
    } finally { setSavingShip(false); }
  };

  const payMethodLabel = (m) => m === "transferencia" ? "Transferencia" : "Link de pago";

  return (
    <div data-testid="admin-orders">
      <div className="mb-10">
        <span className="text-xs uppercase tracking-[0.25em] font-bold text-yolk">─ Pedidos ─</span>
        <h1 className="mt-2 font-heading text-4xl font-bold text-brand-ink">Gestión de pedidos</h1>
      </div>

      <div className="bg-white border-2 border-black rounded-3xl overflow-hidden overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-mint-deep text-left text-xs uppercase tracking-wider text-white border-b-2 border-black">
            <tr>
              <th className="px-6 py-4 font-bold">Cliente</th>
              <th className="px-6 py-4 font-bold">Teléfono</th>
              <th className="px-6 py-4 font-bold">Items</th>
              <th className="px-6 py-4 font-bold">Total</th>
              <th className="px-6 py-4 font-bold">Pago</th>
              <th className="px-6 py-4 font-bold">Comprob.</th>
              <th className="px-6 py-4 font-bold">Envío</th>
              <th className="px-6 py-4 font-bold">Estado</th>
              <th className="px-6 py-4 font-bold">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y-2 divide-black/10">
            {orders.length === 0 ? (
              <tr><td colSpan={9} className="px-6 py-12 text-center text-gray-400">Sin pedidos</td></tr>
            ) : (
              orders.map((o) => (
                <tr key={o.id} data-testid={`order-row-${o.id}`} className="hover:bg-mint-light/40">
                  <td className="px-6 py-4 font-medium text-brand-ink">{o.nombre}</td>
                  <td className="px-6 py-4 text-gray-600">{o.telefono}</td>
                  <td className="px-6 py-4 text-gray-600">{o.items.length}</td>
                  <td className="px-6 py-4 font-bold text-mint-deep">{o.total !== null && o.total !== undefined ? formatCLP(o.total) : "Cotizar"}</td>
                  <td className="px-6 py-4 text-xs">{payMethodLabel(o.payment_method)}</td>
                  <td className="px-6 py-4">
                    {o.comprobante_url ? (
                      <a href={`${BACKEND_URL}${o.comprobante_url}`} target="_blank" rel="noreferrer" data-testid={`order-comprobante-${o.id}`} className="inline-flex items-center gap-1 text-mint-deep hover:underline text-xs">
                        <FileText className="h-3 w-3" /> Ver
                      </a>
                    ) : <span className="text-xs text-gray-400">—</span>}
                  </td>
                  <td className="px-6 py-4 text-xs text-gray-600">
                    {o.shipping_company ? (
                      <span className="inline-flex items-center gap-1"><Truck className="h-3 w-3 text-mint-deep" /> {o.shipping_company}</span>
                    ) : <span className="text-gray-400">—</span>}
                  </td>
                  <td className="px-6 py-4">
                    <select value={o.status} onChange={(e) => updateStatus(o.id, e.target.value)} data-testid={`order-status-${o.id}`} className="text-xs border-2 border-black px-2 py-1 bg-white">
                      {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                  <td className="px-6 py-4 flex items-center gap-2">
                    <button onClick={() => setSelected(o)} data-testid={`order-view-${o.id}`} className="p-2 hover:bg-yolk-light"><Eye className="h-4 w-4" /></button>
                    <button onClick={() => remove(o.id)} data-testid={`order-delete-${o.id}`} className="p-2 hover:bg-red-50"><Trash2 className="h-4 w-4 text-red-500" /></button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-6" onClick={() => setSelected(null)}>
          <div className="bg-white border-2 border-black max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-6 border-b-2 border-black sticky top-0 bg-white z-10">
              <h2 className="font-heading text-xl font-bold">Pedido #{selected.id.slice(0, 8).toUpperCase()}</h2>
              <button onClick={() => setSelected(null)} data-testid="order-modal-close" className="p-2 hover:bg-yolk-light"><X className="h-4 w-4" /></button>
            </div>
            <div className="p-6 space-y-4">
              <D label="Cliente" value={selected.nombre} />
              <D label="RUT" value={selected.rut || "—"} />
              <D label="Teléfono" value={selected.telefono} />
              <D label="Correo" value={selected.correo} />
              <D label="Dirección" value={selected.direccion} />
              <D label="Observaciones" value={selected.observaciones || "—"} />
              <D label="Método de pago" value={payMethodLabel(selected.payment_method)} />

              <div>
                <p className="text-xs uppercase tracking-[0.18em] text-gray-400 mb-3 font-bold">Items</p>
                <div className="border-2 border-black divide-y-2 divide-black">
                  {selected.items.map((it, idx) => (
                    <div key={idx} className="p-3 flex justify-between text-sm">
                      <span>{it.name} × {it.quantity}</span>
                      <span className="text-gray-600">{it.price ? formatCLP(it.price * it.quantity) : "Cotizar"}</span>
                    </div>
                  ))}
                </div>
              </div>

              {(selected.subtotal !== null && selected.subtotal !== undefined) && (
                <div className="bg-mint-light border-2 border-black p-4 space-y-1 text-sm">
                  <div className="flex justify-between"><span>Subtotal</span><span>{formatCLP(selected.subtotal)}</span></div>
                  {selected.discount_global_pct > 0 && (
                    <div className="flex justify-between text-mint-deep"><span>Descuento global (-{selected.discount_global_pct}%)</span><span>−{formatCLP(selected.subtotal * selected.discount_global_pct / 100)}</span></div>
                  )}
                  {selected.discount_code && (
                    <div className="flex justify-between text-mint-deep"><span>Cupón {selected.discount_code} (-{selected.discount_code_pct}%)</span><span>−{formatCLP(selected.subtotal * selected.discount_code_pct / 100)}</span></div>
                  )}
                  <div className="flex justify-between font-bold pt-2 border-t border-black/20"><span>Total</span><span>{selected.total !== null ? formatCLP(selected.total) : "A cotizar"}</span></div>
                </div>
              )}

              {selected.comprobante_url && (
                <div>
                  <p className="text-xs uppercase tracking-[0.18em] text-gray-400 mb-2 font-bold">Comprobante de pago (cliente)</p>
                  <a href={`${BACKEND_URL}${selected.comprobante_url}`} target="_blank" rel="noreferrer" data-testid="modal-comprobante" className="btn-chunky inline-flex items-center gap-2 bg-mint-deep text-white px-4 py-2 text-xs">
                    <Download className="h-3 w-3" /> Ver / descargar comprobante
                  </a>
                </div>
              )}

              {/* SHIPPING FORM */}
              <div className="border-t-2 border-black pt-5 mt-5">
                <div className="flex items-center gap-2 mb-3">
                  <Truck className="h-5 w-5 text-mint-deep" />
                  <h3 className="font-heading text-lg font-bold text-brand-ink">Información de envío</h3>
                </div>
                <p className="text-xs text-gray-600 mb-4">El cliente verá esta información en "Mis pedidos".</p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs uppercase tracking-[0.18em] font-bold text-brand-ink mb-2">Empresa de envío</label>
                    <input type="text" value={shipForm.shipping_company} onChange={(e) => setShipForm({ ...shipForm, shipping_company: e.target.value })} placeholder="Chilexpress, Starken..." data-testid="ship-company" className="w-full border-2 border-black px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-[0.18em] font-bold text-brand-ink mb-2">Código de seguimiento</label>
                    <input type="text" value={shipForm.tracking_code} onChange={(e) => setShipForm({ ...shipForm, tracking_code: e.target.value })} placeholder="ABC123456" data-testid="ship-code" className="w-full border-2 border-black px-3 py-2 text-sm" />
                  </div>
                </div>

                <div className="mt-3">
                  <label className="block text-xs uppercase tracking-[0.18em] font-bold text-brand-ink mb-2">Link de seguimiento (opcional)</label>
                  <input type="text" value={shipForm.tracking_url} onChange={(e) => setShipForm({ ...shipForm, tracking_url: e.target.value })} placeholder="https://www.chilexpress.cl/seguimiento/..." data-testid="ship-url" className="w-full border-2 border-black px-3 py-2 text-sm" />
                </div>

                <div className="mt-3">
                  <label className="block text-xs uppercase tracking-[0.18em] font-bold text-brand-ink mb-2">Foto del comprobante de envío</label>
                  {shipForm.shipping_proof_url && (
                    <div className="mb-3">
                      <img src={shipForm.shipping_proof_url} alt="Comprobante envío" className="h-32 w-auto object-cover border-2 border-black" />
                    </div>
                  )}
                  <label className="btn-chunky inline-flex items-center gap-2 bg-yolk text-black px-4 py-2 cursor-pointer text-sm">
                    <Upload className="h-4 w-4" />
                    {uploading ? "Subiendo..." : shipForm.shipping_proof_url ? "Cambiar foto" : "Subir foto"}
                    <input type="file" accept="image/*" onChange={onUploadProof} className="hidden" data-testid="ship-proof-upload" />
                  </label>
                </div>

                <button onClick={saveShipping} disabled={savingShip} data-testid="ship-save-btn" className="btn-chunky inline-flex items-center gap-2 bg-mint-deep text-white px-5 py-3 text-sm mt-5">
                  <Save className="h-4 w-4" /> {savingShip ? "Guardando..." : "Guardar envío"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const D = ({ label, value }) => (
  <div>
    <p className="text-xs uppercase tracking-[0.18em] text-gray-400 mb-1 font-bold">{label}</p>
    <p className="text-sm text-brand-ink whitespace-pre-wrap">{value}</p>
  </div>
);

export default Orders;
